'use client';

import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Link2, ImageIcon, Share2, Send, Clock, Lock, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { shortlink, DISCLAIMER } from '@/lib/signal/config';
import { track } from '@/lib/analytics';
import { useLang } from '@/lib/i18n/LanguageContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tradeId: string;
  ticker?: string;
  direction?: string;
  status?: string; // 'WIN' → share-your-win mode (result card + caption)
}

type Assets = { square: string | null; story: string | null; pdf: string | null; shortcode: string | null };

// Channels fired client-side at tap (Web Share API on mobile, deep links on desktop). Not rate-limited.
// Instagram has no link-post API → download image + copy caption, then open the app.
const SHARE_TO: { key: string; label: string; color: string }[] = [
  { key: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
  { key: 'viber', label: 'Viber', color: '#7360F2' },
  { key: 'instagram', label: 'Instagram', color: '#E1306C' },
  { key: 'facebook', label: 'Facebook', color: '#1877F2' },
  { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { key: 'reddit', label: 'Reddit', color: '#FF4500' },
];

// Auto-post channels — connected once in Profile (P1), selectable here to drive the Send batch.
const AUTO_POST: { key: string; label: string }[] = [
  { key: 'TELEGRAM', label: 'Telegram' },
  { key: 'DISCORD', label: 'Discord' },
  { key: 'EMAIL', label: 'Email' },
  { key: 'X', label: 'X' },
];

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data: { session } } = await createClient().auth.getSession();
    if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
  } catch {}
  return {};
}

export default function ShareSignalModal({ isOpen, onClose, tradeId, ticker, direction, status }: Props) {
  const { profile } = useAuth();
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<Assets | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number } | null>(null);
  const isWin = status === 'WIN';
  const [winR, setWinR] = useState<number | null>(null);

  const isBuy = ['BUY', 'LONG'].includes(String(direction || '').toUpperCase());

  function toggleAuto(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSelected(new Set());
    // Virality funnel: the share-as-signal sheet opened (referral link is always baked in).
    track('share', { method: 'web_share', content_type: 'idea', item_id: tradeId, has_referral_link: true });
    // Today's auto-broadcast quota (for the Send button state).
    (async () => {
      try {
        const r = await fetch('/api/signal/send', { headers: await authHeaders(), credentials: 'include' });
        if (r.ok && !cancelled) setQuota((await r.json()).quota ?? null);
      } catch {}
    })();
    (async () => {
      setLoading(true); setError(null);
      // Live OG render is always available even if the stored-asset build fails — use it for the
      // preview, image-share and copy-image. Only the PDF download needs the pre-built asset.
      // `v=win` gives the settled card its own CDN cache key (the OPEN-state render is cached for a day).
      const v = isWin ? '&v=win2' : '';
      const liveFallback = (sc?: string | null): Assets => ({
        square: `/api/signal/og?id=${encodeURIComponent(tradeId)}&format=square${v}`,
        story: `/api/signal/og?id=${encodeURIComponent(tradeId)}&format=story${v}`,
        pdf: null,
        shortcode: sc || tradeId.slice(-7),
      });
      if (isWin) {
        // Pre-built assets were rendered while the signal was still open — use the live result card.
        try {
          const [b, r]: any[] = await Promise.all([
            fetch(`/api/trades/${tradeId}/build-assets`, { cache: 'no-store' }).then((x) => (x.ok ? x.json() : {})).catch(() => ({})),
            fetch(`/api/signal/result?id=${encodeURIComponent(tradeId)}`).then((x) => (x.ok ? x.json() : {})).catch(() => ({})),
          ]);
          if (cancelled) return;
          setWinR(typeof r.realizedR === 'number' ? r.realizedR : null);
          setAssets(liveFallback(b.assets?.shortcode));
          setUsername(b.username || profile?.username || null);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      try {
        const res = await fetch(`/api/trades/${tradeId}/build-assets`, { cache: 'no-store' });
        const data = res.ok ? await res.json() : {};
        const uname = data.username;
        let a: Assets;
        if (data.built && data.assets?.square && data.assets?.shortcode) {
          a = data.assets; // cached
        } else {
          // Try the full (owner) build; on ANY failure fall back to the live OG image.
          try {
            const pr = await fetch(`/api/trades/${tradeId}/build-assets`, { method: 'POST', headers: await authHeaders(), credentials: 'include' });
            if (!pr.ok) throw new Error('build failed');
            const built = await pr.json();
            a = built.assets?.square ? built.assets : liveFallback(built.assets?.shortcode || data.assets?.shortcode);
          } catch {
            a = liveFallback(data.assets?.shortcode);
          }
        }
        if (cancelled) return;
        setAssets(a);
        setUsername(uname || profile?.username || null);
      } catch {
        if (!cancelled) { setAssets(liveFallback(null)); setUsername(profile?.username || null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, tradeId, profile?.username, isWin]);

  const refUser = username || profile?.username || '';
  const code = assets?.shortcode || tradeId.slice(-7);
  const baseLink = shortlink(code, refUser || undefined);
  const linkFor = useCallback((ch: string) => `${baseLink}${baseLink.includes('?') ? '&' : '?'}ch=${ch}`, [baseLink]);
  const dirLabel = isBuy ? 'LONG' : 'SHORT';
  const captionText = isWin
    ? `✅ ${ticker || 'Signal'} ${dirLabel} ${t('hit Take Profit')}${winR != null ? ` +${winR}R` : ''} — ${t('my public track record on ShowMeTrade')}\n${DISCLAIMER}`
    : `${ticker || 'Trade'} ${dirLabel} — trade idea on ShowMeTrade\n${DISCLAIMER}`;

  const logShare = useCallback(async (channel: string) => {
    try {
      await fetch('/api/signal/share', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        credentials: 'include', body: JSON.stringify({ tradeId, channel }),
      });
    } catch {}
  }, [tradeId]);

  if (!isOpen) return null;

  async function copyLink() {
    try { await navigator.clipboard.writeText(linkFor('copy')); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 1800); toast.success(t('Link copied')); logShare('copy_link'); }
    catch { toast.error(t('Could not copy')); }
  }

  async function copyImage() {
    if (!assets?.square) { toast.error(t('Asset not ready')); return; }
    try {
      const blob = await (await fetch(assets.square)).blob();
      // @ts-ignore ClipboardItem may be undefined on older browsers
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      toast.success(t('Image copied')); logShare('copy_image');
    } catch { toast.error(t('Image copy unsupported — use Download')); }
  }

  async function downloadImage() {
    if (!assets?.square) { toast.error(t('Asset not ready')); return false; }
    try {
      const blob = await (await fetch(assets.square)).blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(ticker || 'signal')}-showmetrade.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      return true;
    } catch { toast.error(t('Could not download image')); return false; }
  }

  // Auto-broadcast Send (rate-limited). Real senders land in P1; this enforces the cap and
  // reports per-channel connection status.
  async function handleSend() {
    if (selected.size === 0 || sending) return;
    setSending(true);
    try {
      const res = await fetch('/api/signal/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        credentials: 'include', body: JSON.stringify({ tradeId, channels: Array.from(selected) }),
      });
      const data = await res.json();
      if (res.status === 429) { toast.error(data.message || t('Daily auto-broadcast limit reached')); if (data.quota) setQuota(data.quota); return; }
      if (!res.ok) { toast.error(data.error || t('Send failed')); return; }
      if (data.quota) setQuota(data.quota);
      const needConnect = (data.results || []).filter((r: any) => r.status !== 'sent').map((r: any) => r.channel);
      if (needConnect.length) toast(t('Connect these channels in Profile to auto-post: ') + needConnect.join(', '), { icon: '🔌' });
      else toast.success(t('Signal broadcast'));
    } catch { toast.error(t('Network error')); }
    finally { setSending(false); }
  }

  async function deviceShare() {
    const url = linkFor('device');
    try {
      const navAny = navigator as any;
      if (navAny.share) {
        const payload: any = { title: `${ticker} ${dirLabel} · ShowMeTrade`, text: captionText, url };
        if (assets?.square && navAny.canShare) {
          try {
            const file = new File([await (await fetch(assets.square)).blob()], 'signal.png', { type: 'image/png' });
            if (navAny.canShare({ files: [file] })) payload.files = [file];
          } catch {}
        }
        await navAny.share(payload); logShare('device');
      } else { await navigator.clipboard.writeText(url); toast.success(t('Link copied (sharing unsupported here)')); }
    } catch {/* user cancelled */}
  }

  async function shareTo(ch: string) {
    const url = linkFor(ch);
    const text = `${ticker || 'Trade'} ${dirLabel} — ShowMeTrade. ${DISCLAIMER}`;

    // Instagram (and IG/FB feed) have no link-post API → download image + copy caption, open app.
    if (ch === 'instagram') {
      const ok = await downloadImage();
      try { await navigator.clipboard.writeText(`${text}\n${url}`); } catch {}
      if (ok) toast.success(t('Image downloaded + caption copied — open Instagram and paste'));
      logShare('instagram');
      return;
    }

    const encText = encodeURIComponent(text);
    const u = encodeURIComponent(url);
    const map: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      reddit: `https://www.reddit.com/submit?url=${u}&title=${encodeURIComponent(`${ticker} ${dirLabel}`)}`,
      viber: `viber://forward?text=${encodeURIComponent(text + ' ' + url)}`,
    };
    const target = map[ch];
    if (target) { window.open(target, '_blank', 'noopener,noreferrer'); logShare(ch); }
  }

  const cell = 'flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-xs font-semibold transition-colors';

  return (
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto animate-slide-up"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18 }}
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
          <div className="flex items-center gap-2">
            <Share2 size={16} style={{ color: 'var(--accent)' }} />
            <h2 className="font-bold text-sm font-heading" style={{ color: 'var(--text-primary)' }}>{isWin ? t('Share your win 🏆') : t('Share as Signal')}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }} aria-label={t('Close')}><X size={16} /></button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {/* Preview */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent)' }} />
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{t('Preparing your branded signal…')}</p>
            </div>
          ) : error ? (
            <p className="text-sm text-center py-6" style={{ color: 'var(--bearish)' }}>{error}</p>
          ) : (
            <>
              {assets?.square && (
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                  <img src={assets.square} alt="Signal preview" className="w-full block" />
                </div>
              )}
              <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                {t('Your referral link is baked into the QR — every scan & click is tracked')}{refUser ? <> {t('to')} <span style={{ color: 'var(--accent)' }}>@{refUser}</span></> : ''}.
              </p>

              {/* AUTO-POST — select channels to broadcast via Send (connect in Profile — P1) */}
              <div>
                <p className="text-[11px] font-semibold mb-2" style={{ color: 'var(--text-muted)', letterSpacing: '0.06em' }}>{t('AUTO-POST')}</p>
                <div className="grid grid-cols-4 gap-2">
                  {AUTO_POST.map((c) => {
                    const on = selected.has(c.key);
                    return (
                      <button key={c.key} onClick={() => toggleAuto(c.key)} className={cell}
                        style={{
                          background: on ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                          border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                          color: on ? 'var(--accent)' : 'var(--text-secondary)',
                        }}>
                        {on && <Check size={12} />}{c.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                  {quota ? `${quota.used}/${quota.limit} ${t('auto-broadcasts used today.')} ` : ''}
                  {t('Connect channels in Profile to auto-broadcast (coming soon).')}
                </p>
              </div>

              {/* SHARE TO (device / deep links) */}
              <div>
                <p className="text-[11px] font-semibold mb-2" style={{ color: 'var(--text-muted)', letterSpacing: '0.06em' }}>{t('SHARE TO')}</p>
                <div className="grid grid-cols-4 gap-2">
                  {SHARE_TO.map((c) => (
                    <button key={c.key} onClick={() => shareTo(c.key)} className={cell}
                      style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', color: c.color }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-hover)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Utility row */}
              <div className="grid grid-cols-3 gap-2">
                <button onClick={copyLink} className={cell} style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                  {copiedLink ? <Check size={14} style={{ color: 'var(--bullish)' }} /> : <Link2 size={14} />} {copiedLink ? t('Copied') : t('Copy link')}
                </button>
                <button onClick={copyImage} className={cell} style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                  <ImageIcon size={14} /> {t('Copy image')}
                </button>
                <button onClick={deviceShare} className={cell} style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                  <Share2 size={14} /> {t('More')}
                </button>
              </div>

              {assets?.pdf && (
                <a href={assets.pdf} target="_blank" rel="noopener noreferrer" onClick={() => logShare('pdf')}
                  className="text-xs text-center" style={{ color: 'var(--accent)' }}>{t('Download PDF (email / docs)')}</a>
              )}

              {/* Footer: Send (auto-post batch, rate-limited) + Schedule (Pro) */}
              <div className="flex gap-2 pt-1">
                <button onClick={handleSend} disabled={selected.size === 0 || sending}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: selected.size > 0 ? 'var(--accent)' : 'var(--accent-muted)', color: selected.size > 0 ? 'var(--bg-base)' : 'var(--text-muted)', border: '1px solid var(--border)' }}>
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {t('Send')}{selected.size > 0 ? ` (${selected.size})` : ''}
                </button>
                <button disabled title={t('Scheduling is a Pro feature (coming soon)')}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold"
                  style={{ background: 'var(--bg-sunken)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'not-allowed' }}>
                  <Clock size={13} /> {t('Schedule')} <Lock size={10} />
                </button>
              </div>

              <p className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>{DISCLAIMER}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
